import {
  CellId,
  MagicCellEditTypeLiteral,
  MagicEventId,
  MagicEventStatus,
} from "@hex/common";
import { useCallback } from "react";

import { useSelector, useStore } from "../../redux/hooks.js";
import {
  MagicEventMP,
  hexVersionMPSelectors,
} from "../../redux/slices/hexVersionMPSlice.js";
import { shallowEqual } from "../../util/index.js";
import { useProjectContext } from "../../util/projectContext.js";

export interface UseMagicEventsSelectorArgs<T> {
  selector: (magicEvents: Record<MagicEventId, MagicEventMP | undefined>) => T;
  equalityFn?: (left: T, right: T) => boolean;
}

export function useMagicEventsSelector<T>({
  equalityFn,
  selector,
}: UseMagicEventsSelectorArgs<T>): T {
  const { hexVersionId } = useProjectContext();

  return useSelector((state) => {
    const magicEventsState = hexVersionMPSelectors
      .getMagicEventSelectors(hexVersionId)
      .selectEntities(state);

    if (magicEventsState == null) {
      throw new Error(
        `Missing magicEvents state for hex version: ${hexVersionId}`,
      );
    }

    return selector(magicEventsState);
  }, equalityFn ?? shallowEqual);
}

export type UseMagicEventSelectorArgs<T> =
  | {
      magicEventId: MagicEventId;
      selector: (magicEvent: MagicEventMP) => T;
      equalityFn?: (left: T, right: T) => boolean;
      safe?: false;
    }
  | {
      magicEventId: MagicEventId | undefined;
      selector: (magicEvent: MagicEventMP | undefined) => T;
      equalityFn?: (left: T, right: T) => boolean;
      safe: true;
    };

export function useMagicEventSelector<T>(
  args: UseMagicEventSelectorArgs<T>,
): T {
  const { hexVersionId } =
    useProjectContext({ safe: args.safe ?? false }) ?? {};

  return useSelector((state) => {
    if (args.magicEventId == null) {
      if (args.safe) {
        const selector = args.selector;
        return selector(undefined);
      } else {
        throw new Error(`Missing state for magicEvent: ${args.magicEventId}`);
      }
    }
    const magicEventState =
      hexVersionId != null
        ? (hexVersionMPSelectors
            .getMagicEventSelectors(hexVersionId)
            .selectById(state, args.magicEventId) ?? undefined)
        : undefined;

    if (magicEventState == null) {
      if (args.safe) {
        const selector = args.selector;
        return selector(undefined);
      } else {
        throw new Error(`Missing state for magicEvent: ${args.magicEventId}`);
      }
    }

    const selector = args.selector;
    return selector(magicEventState);
  }, args.equalityFn);
}

export type UseLatesteMagicEventForCellSelectorArgs<T> = {
  cellId?: CellId;
  selector: (magicEvent: MagicEventMP | undefined) => T;
  equalityFn?: (left: T, right: T) => boolean;
  safe: boolean;
};

export function useLatestMagicEventForCellSelector<T>(
  args: UseLatesteMagicEventForCellSelectorArgs<T>,
): T {
  const safe = args.safe ?? true;
  const { hexVersionId } = useProjectContext({ safe: safe }) ?? {};

  return useSelector((state) => {
    const cellState =
      hexVersionId != null && args.cellId != null
        ? (hexVersionMPSelectors
            .getCellSelectors(hexVersionId)
            .selectById(state, args.cellId) ?? undefined)
        : undefined;
    if (cellState?.latestMagicEventId == null || hexVersionId == null) {
      if (safe) {
        return args.selector(undefined);
      } else {
        throw new Error(
          `Missing state for magic event for cell: ${args.cellId}`,
        );
      }
    }
    const magicEventState =
      hexVersionMPSelectors
        .getMagicEventSelectors(hexVersionId)
        .selectById(state, cellState.latestMagicEventId) ?? undefined;

    if (magicEventState == null) {
      if (safe) {
        const selector = args.selector;
        return selector(undefined);
      } else {
        throw new Error(`Missing state for cellid: ${args.cellId}`);
      }
    }
    return args.selector(magicEventState);
  }, args.equalityFn);
}

type SafeMagicEventsMP<S extends boolean> = S extends false
  ? Record<MagicEventId, MagicEventMP | undefined>
  : Record<MagicEventId, MagicEventMP | undefined> | undefined;

export type UseMagicEventsGetterArgs<
  ARGS extends unknown[],
  SAFE extends boolean,
  T,
> = {
  selector?: (cells: SafeMagicEventsMP<SAFE>, ...args: ARGS) => T;
  /**
   * @default false
   */
  safe?: SAFE;
};

export type UseMagicEventsGetterResult<ARGS extends unknown[], T> = (
  ...args: ARGS
) => T;

export function useLatestMagicEventForCellGetter() {
  const store = useStore();
  const { hexVersionId } = useProjectContext();

  return useCallback(
    (cellId: CellId) => {
      const state = store.getState();
      const cellState =
        hexVersionMPSelectors
          .getCellSelectors(hexVersionId)
          .selectById(state, cellId) ?? undefined;

      const lastestEventId = cellState?.latestMagicEventId;

      return (
        lastestEventId &&
        hexVersionMPSelectors
          .getMagicEventSelectors(hexVersionId)
          .selectById(state, lastestEventId)
      );
    },
    [store, hexVersionId],
  );
}
/*
 * Determines if a MagicEvent can be run and have its results previewed
 **/
export function canRunPendingMagicEvent(event: MagicEventMP): boolean {
  return (
    event.status === MagicEventStatus.PENDING_REVIEW && // must be pending
    MagicCellEditTypeLiteral.guard(event.eventType) // must be one of insert/edit/fix/etc
  );
}
